import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LongListModule } from '@sbd-doug-frontend/shared/lib/ui/longlist/longlist.module';
import {
  CONTACTS_PROVIDER_TOKEN,
  SharedAccountContactItem,
  SharedContactsService,
} from '@sbd-doug-frontend/shared/lib/shared-services/shared-contacts.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { FilterModule } from '@sbd-doug-frontend/shared/lib/ui/filter/filter.module';
import { AppFilters } from '@sbd-doug-frontend/shared/lib/ui/filter/AppFilters';
import { FilterCollection } from '@sbd-doug-frontend/shared/lib/ui/filter/FilterCollection';
import { QuickSearchData } from '@sbd-doug-frontend/shared/lib/ui/filter/filter.types';
import { FirstNameFilter } from '@sbd-doug-frontend/shared/lib/ui/filter/common-filters/first-name-filter';
import { LastNameFilter } from '@sbd-doug-frontend/shared/lib/ui/filter/common-filters/last-name-filter';
import { EmailFilter } from '@sbd-doug-frontend/shared/lib/ui/filter/common-filters/email-filter';
import { PhoneFilter } from '@sbd-doug-frontend/shared/lib/ui/filter/common-filters/phone-filter';
import { CIPXActionBarComponent } from '@sbd-doug-frontend/shared/lib/ui/action-bar/action-bar.component';
import { CIPXPrimaryButtonComponent } from '@sbd-doug-frontend/shared/lib/ui/buttons/primary-button/primary-button.component';
import { CIPXCancelButtonComponent } from '@sbd-doug-frontend/shared/lib/ui/buttons/cancel-button/cancel-button.component';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { SharedModule } from '@sbd-account-portal/app/shared/shared.module';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { finalize } from 'rxjs/operators';

export type IntakeFormContactsDialogData = {
  preselectedPrincipalContactIds?: number[];
  company_name?: string | null;
};

@Component({
  selector: 'cipx-intake-form-contacts-dialog',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    MatDialogModule,
    LongListModule,
    MatCheckboxModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
    FilterModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatTabsModule,
    MatProgressBarModule,
    CIPXActionBarComponent,
    CIPXCancelButtonComponent,
    CIPXPrimaryButtonComponent,
    MatIconModule,
    ReactiveFormsModule,
  ],
  template: `
    <longlist
      class="h-full w-full"
      [pageTitle]="'Contacts'"
      [drawerOpened]="false"
      [showLongListActions]="true"
      [showRouterOutlet]="false"
      [longlistWithinVtab]="true"
      [showQuickSearch]="!addingContact"
      [showAdvancedSearch]="false"
      [appFilters]="appFilters"
      (quickSearchSelection)="onQuickSearchSelected($event)"
      (quickSearchFocused)="onQuickSearchFocused($event)"
      (filterButtonPressed)="toggleFilterPanel()">
      <longlist-actions class="flex flex-row items-center justify-end gap-2">
        @if (!addingContact) {
          <button *ngIf="!addingContact" mat-stroked-button color="primary" (click)="startAddContact()">
            <mat-icon svgIcon="heroicons_outline:plus"></mat-icon>
            Add Contact
          </button>
        }
        <button color="default" (click)="onCancel()">
          <mat-icon svgIcon="mat_outline:close"></mat-icon>
        </button>
      </longlist-actions>
      <longlist-content>
        <div class="p-4 flex flex-col h-full min-h-0">
          <ng-container *ngIf="!addingContact; else addContactTpl">
            <filter-group
              [appFilters]="appFilters"
              [toggleFilter]="toggleFilter"
              (toggleFilterChange)="toggleFilterPanel()"
              (filterChange)="onFilterChange($event)"
              (removeFilter)="onRemoveFilter($event)"
              (applyFilterButtonPressed)="onApplyFilterButtonPressed($event)"
              [showAdvancedSearch]="false"></filter-group>
            <!-- <div class="text-sm text-gray-500 mb-2">Select contacts to add as listeners</div> -->

            <div class="contacts-grid">
              <div class="md:col-span-2 flex flex-col h-full min-h-0">
                <mat-tab-group
                  class="flex-1 min-h-0 overflow-hidden contact-tabs"
                  (selectedIndexChange)="onTabChange($event)">
                  <mat-tab label="Contacts">
                    <div class="pt-2">
                      <mat-paginator
                        #paginatorTop
                        class="flex justify-end border-b bg-slate-200 dark:bg-slate-900"
                        aria-label="Select page of Contacts"
                        showFirstLastButtons
                        [length]="total"
                        [pageSize]="pageSize"
                        [pageIndex]="pageIndex"
                        [pageSizeOptions]="[25, 50, 100, 1000]"
                        (page)="onPageChange($event)"
                        hidePageSizeOptions
                        hidePageSize></mat-paginator>

                      <mat-progress-bar *ngIf="isLoading" mode="indeterminate"></mat-progress-bar>
                      <div *ngIf="isLoading" class="text-center text-sm text-gray-500 py-2">Loading contacts...</div>

                      <div *ngIf="!isLoading" class="flex-1 min-h-0 overflow-auto">
                        <table
                          mat-table
                          [dataSource]="dataSource"
                          matSort
                          (matSortChange)="onSortChange($event)"
                          class="doug-table">
                          <ng-container matColumnDef="select">
                            <th mat-header-cell *matHeaderCellDef class="align-middle"></th>
                            <td mat-cell *matCellDef="let c" class="align-middle">
                              <mat-checkbox
                                [checked]="isSelected(c.user_id)"
                                (click)="$event.stopPropagation()"
                                (change)="toggle(c.user_id, $event.checked)"></mat-checkbox>
                            </td>
                          </ng-container>

                          <ng-container matColumnDef="info">
                            <th mat-header-cell *matHeaderCellDef class="align-middle" mat-sort-header="user_last_name">
                              Contact
                            </th>
                            <td mat-cell *matCellDef="let c" class="align-middle">
                              <div class="font-medium">{{ c.user_first_name }} {{ c.user_last_name }}</div>
                              @if (c.user_company_label) {
                                <div class="text-xs text-gray-500">{{ c.user_company_label }}</div>
                              }
                              <div class="text-xs text-gray-500">{{ c.user_email_address }}</div>
                              <div class="text-xs text-gray-500">{{ c.user_phone }}</div>
                            </td>
                          </ng-container>

                          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                          <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="onRowClick(row)"></tr>
                          <tr class="mat-row" *matNoDataRow>
                            <td
                              class="mat-cell text-center text-sm text-gray-500"
                              [attr.colspan]="displayedColumns.length">
                              No contacts found.
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </mat-tab>
                  <mat-tab label="My Team">
                    <div class="pt-2">
                      <mat-paginator
                        class="flex justify-end border-b bg-slate-200 dark:bg-slate-900"
                        aria-label="Select page of Account Users"
                        showFirstLastButtons
                        [length]="total"
                        [pageSize]="pageSize"
                        [pageIndex]="pageIndex"
                        [pageSizeOptions]="[25, 50, 100, 1000]"
                        (page)="onPageChange($event)"
                        hidePageSizeOptions
                        hidePageSize></mat-paginator>

                      <mat-progress-bar *ngIf="isLoading" mode="indeterminate"></mat-progress-bar>
                      <div *ngIf="isLoading" class="text-center text-sm text-gray-500 py-2">Loading contacts...</div>

                      <div *ngIf="!isLoading" class="flex-1 min-h-0 overflow-auto">
                        <table
                          mat-table
                          [dataSource]="dataSource"
                          matSort
                          (matSortChange)="onSortChange($event)"
                          class="doug-table">
                          <ng-container matColumnDef="select">
                            <th mat-header-cell *matHeaderCellDef class="align-middle"></th>
                            <td mat-cell *matCellDef="let c" class="align-middle">
                              <mat-checkbox
                                [checked]="isSelected(c.user_id)"
                                (click)="$event.stopPropagation()"
                                (change)="toggle(c.user_id, $event.checked)"></mat-checkbox>
                            </td>
                          </ng-container>

                          <ng-container matColumnDef="info">
                            <th mat-header-cell *matHeaderCellDef class="align-middle" mat-sort-header="user_last_name">
                              Contact
                            </th>
                            <td mat-cell *matCellDef="let c" class="align-middle">
                              <div class="font-medium">{{ c.user_first_name }} {{ c.user_last_name }}</div>
                              @if (c.user_company_label) {
                                <div class="text-xs text-gray-500">{{ c.user_company_label }}</div>
                              }
                              <div class="text-xs text-gray-500">{{ c.user_email_address }}</div>
                              <div class="text-xs text-gray-500">{{ c.user_phone }}</div>
                            </td>
                          </ng-container>

                          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                          <tr mat-row *matRowDef="let row; columns: displayedColumns" (click)="onRowClick(row)"></tr>
                          <tr class="mat-row" *matNoDataRow>
                            <td
                              class="mat-cell text-center text-sm text-gray-500"
                              [attr.colspan]="displayedColumns.length">
                              No results found.
                            </td>
                          </tr>
                        </table>
                      </div>
                    </div>
                  </mat-tab>
                </mat-tab-group>
              </div>

              <div class="sm:visible hidden">
                <p>test</p>
              </div>
              <div class="selected-contacts-container">
                <div class="selected-contacts-header">Selected Contacts ({{ selectedIds().length }})</div>
                <div class="divide-y border rounded bg-white dark:bg-gray-800">
                  @for (c of selectedContactsList; track c.user_id) {
                    <div class="p-3 flex items-start justify-between gap-3">
                      <div class="flex-1">
                        <div class="font-medium">{{ c.user_first_name }} {{ c.user_last_name }}</div>
                        @if (c.user_company_label) {
                          <div class="text-xs text-gray-500">{{ c.user_company_label }}</div>
                        }
                        @if (c.user_email_address) {
                          <div class="text-xs text-gray-500">{{ c.user_email_address }}</div>
                        }
                        @if (c.user_phone) {
                          <div class="text-xs text-gray-500">{{ c.user_phone }}</div>
                        }
                      </div>
                      <button mat-icon-button (click)="removeSelected(c.user_id)" aria-label="Remove">
                        <mat-icon svgIcon="heroicons_outline:trash"></mat-icon>
                      </button>
                    </div>
                  }
                  @if (selectedIds().length === 0) {
                    <div class="p-4 text-xs text-gray-500">No contacts selected.</div>
                  }
                </div>
              </div>
            </div>

            <cipx-action-bar [sticky]="false" [removeStickyOnMobile]="false" [justify]="'end'">
              <cipx-cancel-button (buttonClick)="onCancel()">Cancel</cipx-cancel-button>
              <cipx-primary-button (buttonClick)="onAddSelected()">Update Contacts</cipx-primary-button>
            </cipx-action-bar>
          </ng-container>

          <ng-template #addContactTpl>
            <div class="flex-1 min-h-0 overflow-auto">
              <div class="cipx-form-section">
                <fieldset>
                  <legend class="cipx-form-section-title">Add Contact</legend>
                </fieldset>
              </div>
              <form [formGroup]="addContactForm" class="cipx-form-section">
                <fieldset>
                  <div class="cipx-form-form-group">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <mat-form-field appearance="fill">
                        <mat-label>First Name</mat-label>
                        <input matInput formControlName="user_first_name" />
                      </mat-form-field>
                      <mat-form-field appearance="fill">
                        <mat-label>Last Name</mat-label>
                        <input matInput formControlName="user_last_name" />
                      </mat-form-field>
                    </div>
                    <mat-form-field appearance="fill" class="w-full">
                      <mat-label>Company</mat-label>
                      <input matInput formControlName="user_company_label" />
                    </mat-form-field>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <mat-form-field appearance="fill">
                        <mat-label>Email</mat-label>
                        <input matInput formControlName="user_email_address" />
                        @if (addContactForm.get('user_email_address')?.hasError('required')) {
                          <mat-error>Email is required</mat-error>
                        }
                        @if (addContactForm.get('user_email_address')?.hasError('accountUserEmail')) {
                          <mat-error>
                            This email belongs to a team member on your account. Select them from the My Team tab instead.
                          </mat-error>
                        }
                      </mat-form-field>
                      <mat-form-field appearance="fill">
                        <mat-label>Phone</mat-label>
                        <input matInput formControlName="user_phone" />
                      </mat-form-field>
                    </div>
                  </div>
                </fieldset>
              </form>
            </div>
            <cipx-action-bar [sticky]="false" [removeStickyOnMobile]="false" [justify]="'end'">
              <cipx-cancel-button (buttonClick)="cancelAddContact()">Cancel</cipx-cancel-button>
              <cipx-primary-button (buttonClick)="submitAddContact()" [disabled]="addContactForm.invalid">
                Add Contact
              </cipx-primary-button>
            </cipx-action-bar>
          </ng-template>
        </div>
      </longlist-content>
    </longlist>
  `,
  styles: [
    `
      .contacts-grid {
        @apply flex-1 min-h-0 grid grid-cols-1 md:grid-cols-3 gap-4 overflow-hidden;
      }
      .doug-table {
        .mat-column-select {
          width: 0.5rem !important;
          text-align: center !important;
          align-items: center !important;
        }
        .mat-mdc-checkbox .mdc-form-field {
          padding-right: 0 !important;
        }
      }

      @media (max-width: 600px) {
        .contacts-grid {
          grid-template-rows: 65% 35%;
        }
      }

      .selected-contacts-container {
        @apply md:col-span-1 h-full min-h-0 overflow-auto mt-14;

        .selected-contacts-header {
          --tw-bg-opacity: 1;
          @apply mb-2 p-[18px] bg-gray-200 dark:bg-gray-700;
          // background-color: rgb(226 232 240 / var(--tw-bg-opacity, 1)) !important;
        }
        @media (max-width: 600px) {
          @apply mb-2 mt-0;

          .selected-contacts-header {
            @apply mb-0;
          }
        }
      }
    `,
  ],
  styleUrls: ['../../intake-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IntakeFormContactsDialogComponent implements OnInit {
  private _contactsSvc = inject<SharedContactsService>(CONTACTS_PROVIDER_TOKEN);
  private _cdr = inject(ChangeDetectorRef);
  public contacts: SharedAccountContactItem[] = [];
  public visibleContacts: SharedAccountContactItem[] = [];
  public selectedIds = signal<number[]>([]);
  public displayedColumns: string[] = ['select', 'info'];
  public dataSource = new MatTableDataSource<SharedAccountContactItem>([]);
  public pageSize = 25;
  public pageIndex = 0;
  public total = 0;
  public defaultSort = '';
  public defaultSortDir: 'asc' | 'desc' = 'asc';
  @ViewChild('paginatorTop', { static: false }) paginatorTop!: MatPaginator;
  @ViewChild('paginatorBottom', { static: false }) paginatorBottom!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  public isLoading = false;
  public appFilters: AppFilters = new AppFilters({
    appName: 'contacts_dialog',
    collections: [
      new FilterCollection({
        collectionLabel: 'Contact',
        collection: [
          new FirstNameFilter({ urlQueryParam: 'user_first_name' }),
          new LastNameFilter({ urlQueryParam: 'user_last_name' }),
          new EmailFilter({ urlQueryParam: 'user_email_address' }),
          new PhoneFilter({ urlQueryParam: 'user_phone' }),
        ],
      }),
    ],
  });
  public toggleFilter = false;
  public selectedContactsList: SharedAccountContactItem[] = [];
  public addingContact = false;
  public activeTab: 'contacts' | 'account_users' = 'contacts';
  private _fb = inject(FormBuilder);
  /** Account users on this account; new contacts must not duplicate one of them */
  private _accountUsers: SharedAccountContactItem[] = [];
  public addContactForm = this._fb.group({
    user_first_name: ['', Validators.required],
    user_last_name: ['', Validators.required],
    user_company_label: [''],
    user_email_address: ['', [Validators.required, this.accountUserEmailValidator()]],
    user_phone: [''],
  });

  private accountUserEmailValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const email = String(control.value ?? '').trim().toLowerCase();
      if (!email) return null;
      const isAccountUser = this._accountUsers.some(
        (u) => String(u.user_email_address ?? '').trim().toLowerCase() === email
      );
      return isAccountUser ? { accountUserEmail: true } : null;
    };
  }

  constructor(
    public dialogRef: MatDialogRef<IntakeFormContactsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IntakeFormContactsDialogData
  ) {}

  ngOnInit(): void {
    this.dialogRef.keydownEvents().subscribe((event) => {
      const keyboardEvent = event as KeyboardEvent;
      if (keyboardEvent.key === 'Escape' || keyboardEvent.key === 'Esc') {
        keyboardEvent.stopPropagation();
        keyboardEvent.preventDefault();
        this.onCancel();
      }
    });

    if (Array.isArray(this.data?.preselectedPrincipalContactIds)) {
      this.selectedIds.set([...(this.data.preselectedPrincipalContactIds ?? [])]);
    }
    if (this.data?.company_name) {
      this.addContactForm.patchValue({ user_company_label: this.data.company_name });
    }
    this.fetchContacts();
    // Also populate selected list from the other tab type so preselected account users show up
    this.fetchOtherTypeSelected();

    // Load account users up front so Add Contact can reject emails that belong to one
    this._contactsSvc
      .getContacts({ contact_type: 'account_users', limit: 1000, offset: 0 })
      .subscribe(({ contacts }) => {
        this._accountUsers = contacts ?? [];
        this.addContactForm.get('user_email_address')?.updateValueAndValidity();
      });
  }

  private fetchOtherTypeSelected(): void {
    const ids = new Set(this.selectedIds());
    if (ids.size === 0) return;
    const otherType = this.activeTab === 'contacts' ? 'account_users' : 'contacts';
    const params: Record<string, any> = {
      contact_type: otherType,
      limit: 1000,
      offset: 0,
    };
    this._contactsSvc.getContacts(params).subscribe(({ contacts }) => {
      const matches = (contacts || []).filter((c) => ids.has(c.user_id));
      matches.forEach((c) => {
        if (!this.selectedContactsList.some((x) => x.user_id === c.user_id)) {
          this.selectedContactsList = [...this.selectedContactsList, c];
        }
      });
      this._cdr.markForCheck();
    });
  }

  startAddContact() {
    this.addingContact = true;
    this._cdr.markForCheck();
  }

  cancelAddContact() {
    this.addingContact = false;
    this._cdr.markForCheck();
  }

  submitAddContact() {
    if (this.addContactForm.invalid) return;
    const raw = this.addContactForm.getRawValue();
    const payload: Partial<SharedAccountContactItem> = {
      user_first_name: raw.user_first_name || undefined,
      user_last_name: raw.user_last_name || undefined,
      user_company_label: raw.user_company_label || undefined,
      user_email_address: raw.user_email_address || undefined,
      user_phone: raw.user_phone || undefined,
    };
    this._contactsSvc.createContact(payload).subscribe((created) => {
      this.addingContact = false;
      this.addContactForm.reset({ user_company_label: this.data?.company_name ?? '' });

      if (created) {
        // Prepend to current arrays
        this.contacts = [created, ...this.contacts];
        this.visibleContacts = this.contacts;
        this.dataSource.data = this.visibleContacts;
        this.total = (this.total || 0) + 1;

        // Mark as selected
        const id = created.user_id;
        const cur = new Set(this.selectedIds());
        cur.add(id);
        this.selectedIds.set(Array.from(cur));
        if (!this.selectedContactsList.some((x) => x.user_id === id)) {
          this.selectedContactsList = [created, ...this.selectedContactsList];
        }
      }

      this._cdr.markForCheck();
    });
  }

  isSelected(id: number): boolean {
    return this.selectedIds().includes(id);
  }

  toggle(id: number, checked: boolean): void {
    const cur = new Set(this.selectedIds());
    if (checked) cur.add(id);
    else cur.delete(id);
    this.selectedIds.set(Array.from(cur));
    // Maintain selected list visible on the right
    if (checked) {
      const item = this.contacts.find((x) => x.user_id === id);
      if (item && !this.selectedContactsList.some((x) => x.user_id === id)) {
        this.selectedContactsList = [...this.selectedContactsList, item];
      }
    } else {
      this.selectedContactsList = this.selectedContactsList.filter((x) => x.user_id !== id);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onAddSelected(): void {
    const ids = new Set(this.selectedIds());
    const selected = this.selectedContactsList.filter((c) => ids.has(c.user_id));
    this.dialogRef.close({ selected });
  }

  // Legacy element-level handler retained for safety if needed elsewhere
  onEsc(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.onCancel();
  }

  onQuickSearchSelected(event: QuickSearchData) {
    this.appFilters.onQuickSearchSelected(event);
    this.applyFilter(false);
  }

  onQuickSearchFocused(_focused: boolean) {
    /* no-op */
  }

  toggleFilterPanel() {
    this.toggleFilter = !this.toggleFilter;
    this._cdr.detectChanges();
  }

  onFilterChange(_fg: unknown) {
    this.applyFilter(false);
  }

  onRemoveFilter(_fg: unknown) {
    this.applyFilter(false);
  }

  onApplyFilterButtonPressed(_toggle: boolean) {
    this.applyFilter(false);
  }

  private applyFilter(_toggleFilterPanel = false) {
    // Server-side filtering & pagination
    this.fetchContacts();
  }

  private fetchContacts(): void {
    const filters = this.appFilters.getAppliedFiltersURLParameters() || {};
    const params: Record<string, any> = { ...filters };
    params['limit'] = this.pageSize;
    params['pageIndex'] = this.pageIndex;
    params['offset'] = this.pageSize * this.pageIndex;
    params['contact_type'] = this.activeTab === 'contacts' ? 'contacts' : 'account_users';
    if (this.sort && this.sort.active && this.sort.direction) {
      params['sort_by'] = `${this.sort.active}.${this.sort.direction}`;
    }

    this.isLoading = true;
    // Clear current rows so previous tab’s rows don’t linger while loading
    this.visibleContacts = [];
    this.dataSource.data = [];
    this._cdr.markForCheck();

    this._contactsSvc
      .getContacts(params)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe(({ contacts, total }) => {
        this.contacts = Array.isArray(contacts) ? contacts : [];
        this.total = Number.isFinite(total) ? total : this.contacts.length;
        this.visibleContacts = this.contacts;
        this.dataSource.data = this.visibleContacts;
        // Augment selected list with any selected items present in the current page
        const currentSelected = new Set(this.selectedIds());
        const addable = this.contacts.filter((c) => currentSelected.has(c.user_id));
        addable.forEach((c) => {
          if (!this.selectedContactsList.some((x) => x.user_id === c.user_id)) {
            this.selectedContactsList = [...this.selectedContactsList, c];
          }
        });
        if (this.paginatorTop) {
          this.paginatorTop.length = this.total;
          this.paginatorTop.pageSize = this.pageSize;
          this.paginatorTop.pageIndex = this.pageIndex;
        }
        if (this.paginatorBottom) {
          this.paginatorBottom.length = this.total;
          this.paginatorBottom.pageSize = this.pageSize;
          this.paginatorBottom.pageIndex = this.pageIndex;
        }
        this._cdr.markForCheck();
      });
  }

  onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    if (this.paginatorBottom) {
      this.paginatorBottom.pageSize = event.pageSize;
      this.paginatorBottom.pageIndex = event.pageIndex;
    }
    this.fetchContacts();
  }

  onSortChange(event: Sort) {
    if ('active' in event) {
      this.defaultSort = event.active || '';
      this.defaultSortDir = (event.direction || 'asc') as 'asc' | 'desc';
      this.pageIndex = 0;
      this.fetchContacts();
    }
  }

  removeSelected(id: number): void {
    const cur = new Set(this.selectedIds());
    cur.delete(id);
    this.selectedIds.set(Array.from(cur));
    this.selectedContactsList = this.selectedContactsList.filter((x) => x.user_id !== id);
  }

  onPageChangeBottom(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    if (this.paginatorTop) {
      this.paginatorTop.pageSize = event.pageSize;
      this.paginatorTop.pageIndex = event.pageIndex;
      // Emit to update dataSource paginator page state
      this.paginatorTop.page.emit(event);
    }
    this.fetchContacts();
  }

  onRowClick(c: SharedAccountContactItem): void {
    const id = c.user_id;
    const next = !this.isSelected(id);
    this.toggle(id, next);
  }

  onTabChange(index: number) {
    this.activeTab = index === 0 ? 'contacts' : 'account_users';
    this.pageIndex = 0;
    this.addingContact = false;
    this.fetchContacts();
    // Ensure selected list contains items from the other type as well
    this.fetchOtherTypeSelected();
  }
}
